import React, { ComponentProps } from 'react';
import Avatar, { DefaultAvatar } from '@adsk/alloy-react-avatar';
import { StylableComponent } from '@adsk/alloy-react-theme';
import { BuildingFilledIcon, PeopleFilledIcon } from '@adsk/alloy-react-icon';
import Tooltip from '@adsk/alloy-react-tooltip';
import { Assignee, AssigneeType } from '../sdk';
import { useOxygen } from '../providers/OxygenProvider';
import { getAssigneeDisplayName, getAssigneeImageUrl } from '../helpers';
import { ToolTipContent } from './AssigneeCell/components';

const getTargetProps = () => ({ shouldWrapChildren: false });

const renderEntityIcon = (assigneeType: AssigneeType, size: number) => {
  switch (assigneeType) {
    case AssigneeType.Role:
      return <PeopleFilledIcon size={size} />;
    case AssigneeType.Company:
      return <BuildingFilledIcon size={size} />;
    case AssigneeType.User:
    default:
      return null;
  }
};

type AvatarProps = ComponentProps<typeof Avatar>;
export type AssigneeAvatarProps = AvatarProps &
  Omit<StylableComponent<HTMLSpanElement>, 'color'> & {
    assignee?: Assignee;
    rolesAndCompaniesIconSize?: number;
    includeTooltip?: boolean;
    isTargetRfi?: boolean;
  };

const AssigneeAvatar = React.forwardRef<HTMLSpanElement, AssigneeAvatarProps>(
  (
    { assignee, rolesAndCompaniesIconSize = 16, includeTooltip = false, isTargetRfi, ...props },
    ref,
  ) => {
    const oxygen = useOxygen();

    if (!assignee) {
      return <DefaultAvatar size={props.size} style={props.style ? props.style : undefined} />;
    }

    const displayName = getAssigneeDisplayName({ oxygen, assignee, isTargetRfi });
    // The image is displayed only for users. Icons are displayed for companies and roles.
    const imageUrl =
      assignee.type === AssigneeType.User ? getAssigneeImageUrl(oxygen, assignee) : '';

    const renderAvatar = () => (
      <Avatar ref={ref} name={displayName} image={imageUrl} {...props}>
        {renderEntityIcon(assignee.type, rolesAndCompaniesIconSize)}
      </Avatar>
    );

    if (includeTooltip) {
      return (
        <Tooltip
          content={<ToolTipContent assignees={[assignee]} isTargetRfi={isTargetRfi} />}
          getTargetProps={getTargetProps}
        >
          {renderAvatar()}
        </Tooltip>
      );
    }

    return renderAvatar();
  },
);

AssigneeAvatar.displayName = 'AssigneeAvatar';

export default Object.assign(AssigneeAvatar, {
  Sizes: Avatar.SIZES,
  Colors: Avatar.COLORS,
});
